import { Box, Grid, Link, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import Flex from "../../../../components/Flex";
import FormattedText from "../../../../components/FormattedText";
import MultiSpecialty from "../../../../components/MultiSpecialty";
import OutlineCard from "../../../../components/OutlineCard";
import ProfileViewer from "../../../../components/ProfileViewer";
import { CustomTrimmedTooltip } from "../../../../components/RenderWithTooltip";
import Text from "../../../../components/Text";
import { TIME_ZONE_MAPPER } from "../../../../constants";
import { useGetProviderGroupProfile } from "../../../../queries/admin/provider-group/use-get-provider-group-profile";
import { useProviderGroupProfile } from "../../../../queries/admin/provider-group/use-provider-group-profile";
import CopyButton from "../../../../utils/util-components/CopyButton";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_PROVIDER_GROUP_IMG_SRC,
  getAddressFormat,
  transformOfficeHoursToView,
} from "../../../../utils/utils";
import { ScrollArea } from "../../../../components/ScrollBar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

const ProviderGroupProfile = () => {
  const { uuid } = useParams();
  const viewAs = getViewAs();
  const realm = getStoredRealm();

  const { data: providerSidePgProfileData }: any = useGetProviderGroupProfile(
    realm ?? undefined,
    viewAs !== "provider"
  );

  const { data: adminSidePgProfileData }: any = useProviderGroupProfile(uuid);

  const profileData =
    viewAs === "provider" ? providerSidePgProfileData : adminSidePgProfileData;

  const insuranceAcceptedViewList = Object.values(
    profileData?.acceptedInsurances ?? {}
  ).map((it: any) => it);

  return (
    <Flex width={"100%"} gap={2} mt={"20px"}>
      <Box width={"600px"} minHeight={"575px"}>
        <OutlineCard sx={{ height: "100%", width: "100%" }}>
          <Flex gap={4} my={1.5} flexDirection={"column"}>
            <Grid item>
              <img
                src={profileData?.avatar ?? DEFAULT_PROVIDER_GROUP_IMG_SRC}
                style={{ borderRadius: 8 }}
                width={"140px"}
                height={"135px"}
              />
              <Typography fontWeight={"bold"} fontSize={26} mb={2.8} mt={1.2}>
                {profileData?.name}
              </Typography>
              <MultiSpecialty
                specialties={profileData?.specialities}
                width={280}
              />
            </Grid>
            <ProfileViewer
              gap={"18px"}
              withoutColn
              firstColW={4.5}
              secondColW={7.5}
              data={{
                group_NPI_number: profileData?.npi,
                website: profileData?.website,
                contact_number: profileData?.phone,
                email: profileData?.email,
                fax_number: profileData?.fax,
                Time_Zone: TIME_ZONE_MAPPER[profileData?.timezone ?? ""],
                physical_address: {
                  true: {
                    label: getAddressFormat(profileData?.physicalAddress),
                    trimmed: 95,
                  },
                },
                Login_URL: {
                  custom: () => (
                    <Flex>
                      <Link underline="hover" sx={{ overflow: "hidden" }}>
                        <a href={profileData?.loginUrl} target="_blank">
                          <CustomTrimmedTooltip value={profileData?.loginUrl} />
                        </a>
                      </Link>

                      <CopyButton
                        onClick={() => {
                          navigator.clipboard.writeText(profileData?.loginUrl);
                        }}
                      />
                    </Flex>
                  ),
                },
              }}
            />
            {/* <Grid container item>
              <Grid item xs={4.5}>
                <Text fontWeight={"medium"} color={"#1A1A1A80"}>
                  Login URL
                </Text>
              </Grid>
              <Grid item gap={"18px"} xs={7.5} display={"inline-block"}>
                <StyledText>
                  <Link underline="hover">
                    <a href={profileData?.loginUrl} target="_blank">
                      {`: ${profileData?.loginUrl}`}
                    </a>
                  </Link>
                  <CopyButton
                    onClick={() => {
                      navigator.clipboard.writeText(profileData?.loginUrl);
                    }}
                  />
                </StyledText>
              </Grid>
            </Grid> */}
          </Flex>
        </OutlineCard>
      </Box>

      <ScrollArea height={"calc(100vh - 12rem)"}>
        <Box minHeight={"575px"} width={"100%"}>
          <OutlineCard sx={{ height: "100%", width: "100%" }}>
            <Grid container columnSpacing={2}>
              {/* Left column: Basic Information */}
              <Grid item xs={12} md={7}>
                <Flex flexDirection={"column"}>
                  <Text variant="h4" fontWeight={"medium"} marginTop={"10px"}>
                    Basic Information
                  </Text>
                  <Grid container mt={2} wrap="wrap">
                    <Grid item xs={4} sm={3} lg={2.2} mt={2.8}>
                      <Typography
                        fontWeight={"medium"}
                        color={"#1A1A1A80"}
                        fontSize={DEFAULT_FONTSIZE}
                      >
                        Billing Address
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={7} md={8} lg={9.8} mt={2.8}>
                      <Typography fontSize={DEFAULT_FONTSIZE}>
                        : {getAddressFormat(profileData?.billingAddress)}
                      </Typography>
                    </Grid>

                    <Grid item xs={4} sm={3} lg={2.2} mt={2.8}>
                      <Typography
                        fontWeight={"medium"}
                        color={"#1A1A1A80"}
                        fontSize={DEFAULT_FONTSIZE}
                      >
                        Office Hours
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={7} md={8} lg={9.8} mt={2.8}>
                      <Box>
                        {(
                          transformOfficeHoursToView(
                            profileData?.officeHours
                          ) as any
                        )?.map((it: any) => {
                          const [day, time] = it?.split("|");

                          return (
                            <Flex gap={1} mt={1}>
                              :
                              <Typography
                                fontSize={DEFAULT_FONTSIZE}
                                width={"80px"}
                              >
                                {day}
                              </Typography>
                              <Flex gap={1}>
                                <Text variant="subtitle2">|</Text>
                                <Typography fontSize={DEFAULT_FONTSIZE}>
                                  {time}
                                </Typography>
                              </Flex>
                            </Flex>
                          );
                        }) ?? ""}
                      </Box>
                    </Grid>
                    <Grid item xs={4} sm={3} lg={2.2} mt={2.8}>
                      <Typography
                        fontWeight={"medium"}
                        color={"#1A1A1A80"}
                        fontSize={DEFAULT_FONTSIZE}
                      >
                        Provider Group Information
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={7}
                      md={8}
                      lg={9.8}
                      mt={2.8}
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    >
                      <Typography
                        fontSize={DEFAULT_FONTSIZE}
                        component="span"
                        mr={1}
                      >
                        :{" "}
                      </Typography>
                      {profileData?.description && (
                        <FormattedText value={profileData?.description} />
                      )}
                    </Grid>
                  </Grid>
                </Flex>
              </Grid>
              {/* Right column: Insurance(s) Accepted */}
              <Grid item xs={12} md={5}>
                <Box
                  p={3}
                  pl={4}
                  pt={8}
                  pr={0}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  height="100%"
                >
                  <Typography
                    fontWeight="medium"
                    color="#1A1A1A80"
                    fontSize={DEFAULT_FONTSIZE}
                    mb={2}
                  >
                    Insurance(s) Accepted
                  </Typography>
                  <Box ml={1}>
                    {insuranceAcceptedViewList?.length > 0 ? (
                      <Box>
                        {insuranceAcceptedViewList.map((it, ind) => (
                          <Flex key={ind} alignItems="flex-start" mb={0.5}>
                            <Text mr={1}>{ind + 1}.</Text>
                            <Text>{it}</Text>
                          </Flex>
                        ))}
                      </Box>
                    ) : (
                      <Text>-</Text>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </OutlineCard>
        </Box>
      </ScrollArea>
    </Flex>
  );
};

export default ProviderGroupProfile;
